import { RawAvatar, AvatarSize } from "@components/Avatar";
import {
  Button,
  ButtonTargetKind,
  ButtonRole,
  ButtonSize,
} from "@components/Button";
import { Checkbox } from "@components/Checkbox";
import { css } from "@emotion/react";

import { ReactComponent as PlaceholderIllustration } from "src/assets/illustrations/nonprofit.svg";
import { useNonprofit } from "src/context/NonprofitsContext/hooks";
import { NonprofitFetchStatus } from "src/context/NonprofitsContext/types";
import { colorCssVars } from "src/theme/color";
import { spacing, verticalStackCss } from "src/theme/spacing";
import { textSizeCss, FontWeight } from "src/theme/text";

interface FundraiserNonprofitslistProps {
  nonprofitIds: string[];
  onRemove?: (id: string) => void;
  weights?: number[];
  checkedIds?: string[];
  onToggle?: (id: string) => void;
}

export const FundraiserNonprofitsList = ({
  nonprofitIds,
  onRemove,
  weights,
  checkedIds,
  onToggle,
}: FundraiserNonprofitslistProps) => {
  if (!nonprofitIds || nonprofitIds.length === 0) {
    return null;
  }

  const normalizedPcts =
    weights && weights.length === nonprofitIds.length
      ? (() => {
          const sum = weights.reduce((a, b) => a + b, 0);
          if (sum <= 0) {
            return undefined;
          }
          // Work in hundredths of a percent (scale of 10000) then floor each,
          // assigning the leftover to the first nonprofit — mirrors the
          // leftover-to-first logic in splitMultiNonprofitCharge.
          const scale = 10000;
          const floored = weights.map((w) => Math.floor((w / sum) * scale));
          floored[0] += scale - floored.reduce((a, b) => a + b, 0);
          return floored.map((v) => v / 100);
        })()
      : undefined;

  return (
    <div css={spacing.xs}>
      <p
        css={[
          textSizeCss.xs,
          {
            color: `var(${colorCssVars.text.secondary})`,
            marginBottom: spacing.s,
          },
        ]}
      >
        This fundraiser supports the following nonprofits:
      </p>
      <ul css={verticalStackCss.s}>
        {nonprofitIds.map((id, i) => (
          <ListItem
            key={id}
            nonprofitId={id}
            onRemove={onRemove}
            percentage={normalizedPcts?.[i]}
            checked={checkedIds ? checkedIds.includes(id) : undefined}
            onToggle={onToggle}
          />
        ))}
      </ul>
    </div>
  );
};

interface ListItemProps {
  nonprofitId: string;
  onRemove?: (id: string) => void;
  percentage?: number;
  checked?: boolean;
  onToggle?: (id: string) => void;
}

const ListItem = ({
  nonprofitId,
  onRemove,
  percentage,
  checked,
  onToggle,
}: ListItemProps) => {
  const nonprofit = useNonprofit({ id: nonprofitId });

  if (nonprofit === NonprofitFetchStatus.NONPROFIT_NOT_FOUND) {
    return null;
  }

  if (nonprofit === NonprofitFetchStatus.FETCHING_NONPROFIT) {
    return (
      <li
        css={css`
          display: flex;
          align-items: center;
          gap: ${spacing.xs};
          position: relative;
        `}
      >
        <RawAvatar
          size={AvatarSize.XX_SMALL}
          placeholder={<PlaceholderIllustration />}
        />
        <span
          css={[
            textSizeCss.xs,
            { fontWeight: FontWeight.MEDIUM, lineHeight: "1.25" },
          ]}
        >
          Loading...
        </span>
      </li>
    );
  }

  return (
    <li
      key={nonprofit.id}
      css={css`
        display: flex;
        align-items: center;
        gap: ${spacing.xs};
        position: relative;
      `}
    >
      {onToggle && (
        <Checkbox
          data-tname="nonprofit-checkbox"
          checked={!!checked}
          onChange={() => onToggle(nonprofit.id)}
        />
      )}
      <RawAvatar
        size={AvatarSize.XX_SMALL}
        cloudinaryId={nonprofit.logoCloudinaryId}
        placeholder={<PlaceholderIllustration />}
      />
      <span
        css={[
          textSizeCss.xs,
          { fontWeight: FontWeight.MEDIUM, lineHeight: "1.25" },
        ]}
      >
        {nonprofit.name}
        {percentage !== undefined ? ` (${percentage}%)` : ""}
      </span>
      {onRemove && (
        <Button
          onClick={{
            kind: ButtonTargetKind.FUNCTION,
            action: () => onRemove(nonprofit.id),
          }}
          data-tname="remove-nonprofit"
          role={ButtonRole.TEXT_SECONDARY}
          size={ButtonSize.SMALL}
          css={textSizeCss.xs}
        >
          Remove
        </Button>
      )}
    </li>
  );
};
